import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { useGetCustom } from "@/api/customs.api";
import { useGetCustomPolicy } from "@/api/customPolicy.api";
import { Modal } from "@/components/ui/customs/modal";
import { Separator } from "@/components/ui/separator";
import { currenciesOptions } from "@/constants/options/currencies.option";
import { ImportLicenseRequiredEnum } from "@/constants/enums/importLicenseRequired.enum";
import { IdentityDocumentRequiredEnum } from "@/constants/enums/identityDocumentRequired.enum";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { GetCustomPolicyDto, GetCustomResponse } from "@/types/api/api-types";
import { cn } from "@/lib/utils";
import { Loader2 } from "lucide-react";
import {
  useIdentityDocumentRequiredLabels,
  useImportLicenseRequiredLabels,
} from "@/hooks/useEnumLabels";

type Props = {
  open: boolean;
  onClose: () => void;
  customId: string;
};

function policyText(value: unknown): string | null {
  if (value == null) return null;
  if (typeof value === "string") {
    const t = value.trim();
    return t.length > 0 ? t : null;
  }
  return null;
}

function currencyLabel(code: string): string {
  return currenciesOptions.find((c) => c.value === code)?.label ?? code;
}

function formatNumFr(n: number, opts?: Intl.NumberFormatOptions): string {
  return new Intl.NumberFormat("fr-FR", opts).format(n);
}

function CompactRow({
  label,
  value,
  className,
}: {
  label: string;
  value: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex gap-2 text-sm leading-snug", className)}>
      <span className="min-w-[9.5rem] shrink-0 text-xs font-medium text-gray-500">{label}</span>
      <span className="min-w-0 flex-1 font-medium text-gray-900">{value}</span>
    </div>
  );
}

function restrictionSummary(active: boolean, value: number, suffix?: string): string {
  if (!active) return "—";
  const base = formatNumFr(value);
  return suffix ? `${base} ${suffix}` : base;
}

function CustomPolicyModalContent({
  custom,
  policy,
}: {
  custom: GetCustomResponse;
  policy: GetCustomPolicyDto;
}) {
  const { t } = useTranslation(["boExpeditionForm", "common"]);
  const yn = (v: boolean): string => (v ? t("common:boolean.yes") : t("common:boolean.no"));
  const importLicenseRequiredLabels = useImportLicenseRequiredLabels();
  const identityDocumentRequiredLabels = useIdentityDocumentRequiredLabels();
  const notes = policyText(policy.notes);
  const procedure = policyText(policy.procedure);
  const importLicense =
    importLicenseRequiredLabels[policy.importLicenseRequired as ImportLicenseRequiredEnum] ??
    policy.importLicenseRequired;
  const identityDocument =
    identityDocumentRequiredLabels[
      policy.identityDocumentRequired as IdentityDocumentRequiredEnum
    ] ?? policy.identityDocumentRequired;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50/80 px-3 py-2">
        <div className="flex min-w-0 items-center gap-2">
          {custom.flagCode ? (
            <img
              src={`http://purecatamphetamine.github.io/country-flag-icons/3x2/${custom.flagCode.toUpperCase()}.svg`}
              className="h-4 w-6 shrink-0 rounded-sm object-cover"
              alt=""
            />
          ) : null}
          <span className="truncate text-sm font-semibold text-gray-900">{custom.label}</span>
        </div>
        <span className="text-xs text-gray-500">
          {t("customPolicyModal.updatedAt", {
            date: format(new Date(policy.updatedAt), "dd/MM/yyyy 'à' HH:mm", { locale: fr }),
          })}
        </span>
      </div>

      <div className={cn("grid gap-5", (notes || procedure) && "lg:grid-cols-2 lg:gap-x-8")}>
        <div className="flex flex-col gap-5">
          <section className="space-y-2">
            <h4 className="text-xs font-semibold tracking-wide text-gray-500 uppercase">
              {t("customPolicyModal.restrictionsTitle")}
            </h4>
            <div className="space-y-1.5 rounded-lg border border-gray-200 bg-white p-3">
              <CompactRow
                label={t("customPolicyModal.bottlesLabel")}
                value={restrictionSummary(policy.maxBottles, policy.maxBottlesValue)}
              />
              <CompactRow
                label={t("customPolicyModal.litersLabel")}
                value={restrictionSummary(policy.maxLiters, policy.maxLitersValue, "L")}
              />
              <CompactRow
                label={t("customPolicyModal.purchaseValueLabel")}
                value={
                  policy.maxPurchase
                    ? `${formatNumFr(policy.maxPurchaseValue)} ${currencyLabel(policy.maxPurchaseCurrency)}`
                    : "—"
                }
              />
              <CompactRow
                label={t("customPolicyModal.alcoholLabel")}
                value={restrictionSummary(
                  policy.maxAlcoholPercentage,
                  policy.maxAlcoholPercentageValue,
                  "%",
                )}
              />
              <Separator className="my-2" />
              <CompactRow
                label={t("customPolicyModal.divideOverDaysLabel")}
                value={yn(policy.divideShipmentOverDays)}
              />
              <CompactRow label={t("customPolicyModal.dapOnlyLabel")} value={yn(policy.dapOnly)} />
              <CompactRow
                label={t("customPolicyModal.noPrivateShipmentLabel")}
                value={yn(policy.noShipmentToPrivateIndividual)}
              />
              <CompactRow
                label={t("customPolicyModal.sampleOnlyLabel")}
                value={yn(policy.sampleOnly)}
              />
              <CompactRow
                label={t("customPolicyModal.freightLabel")}
                value={
                  policy.freightAvailable
                    ? t("customPolicyModal.freightAvailable")
                    : t("customPolicyModal.freightUnavailable")
                }
              />
            </div>
          </section>

          <section className="space-y-2">
            <h4 className="text-xs font-semibold tracking-wide text-gray-500 uppercase">
              {t("customPolicyModal.documentsTitle")}
            </h4>
            <div className="space-y-1.5 rounded-lg border border-gray-200 bg-white p-3">
              <CompactRow label={t("customPolicyModal.importLicenseLabel")} value={importLicense} />
              <CompactRow
                label={t("customPolicyModal.identityDocumentLabel")}
                value={identityDocument}
              />
              <CompactRow
                label={t("customPolicyModal.originCertificateLabel")}
                value={yn(policy.originCertificateRequired)}
              />
              <CompactRow
                label={t("customPolicyModal.powerOfAttorneyLabel")}
                value={yn(policy.powerOfAttorneyRequired)}
              />
              <CompactRow
                label={t("customPolicyModal.otherDocumentsLabel")}
                value={
                  policy.otherRequiredDocuments?.length
                    ? policy.otherRequiredDocuments.join(", ")
                    : "—"
                }
              />
            </div>
          </section>
        </div>

        {(notes || procedure) && (
          <div className="flex flex-col gap-5">
            {procedure ? (
              <section className="space-y-2">
                <h4 className="text-xs font-semibold tracking-wide text-gray-500 uppercase">
                  {t("customPolicyModal.procedureTitle")}
                </h4>
                <div className="max-h-[28rem] overflow-y-auto rounded-lg border border-gray-200 bg-white p-3 text-sm leading-snug whitespace-pre-wrap text-gray-800">
                  <p>{procedure}</p>
                </div>
              </section>
            ) : null}
            {notes ? (
              <section className="space-y-2">
                <h4 className="text-xs font-semibold tracking-wide text-gray-500 uppercase">
                  {t("customPolicyModal.notesTitle")}
                </h4>
                <div className="max-h-36 overflow-y-auto rounded-lg border border-gray-200 bg-white p-3 text-sm leading-snug whitespace-pre-wrap text-gray-800">
                  <p>{notes}</p>
                </div>
              </section>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

export function CustomPolicyModal({ open, onClose, customId }: Props) {
  const { t } = useTranslation("boExpeditionForm");
  const {
    data: custom,
    isLoading: isLoadingCustom,
    isError: isErrorCustom,
  } = useGetCustom(customId);
  const {
    data: policy,
    isLoading: isLoadingPolicy,
    isError: isErrorPolicy,
  } = useGetCustomPolicy(customId);

  const loading = isLoadingCustom || isLoadingPolicy;
  const error = isErrorCustom || isErrorPolicy;
  const ready = custom && policy && !loading && !error;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("customPolicyModal.title")}
      dismissible
      size="lg"
      contentClassName="flex flex-col gap-4"
    >
      {loading ? (
        <div className="flex min-h-[220px] flex-col items-center justify-center gap-3 py-6">
          <Loader2 className="animate-spin" />
          <span className="text-sm text-gray-500">{t("customPolicyModal.loading")}</span>
        </div>
      ) : error || !ready ? (
        <div className="flex min-h-[160px] flex-col items-center justify-center gap-2 py-6 text-center">
          <span className="text-sm font-semibold text-red-600">
            {t("customPolicyModal.loadError")}
          </span>
          <span className="max-w-md text-xs text-gray-500">
            {t("customPolicyModal.loadErrorHelp")}
          </span>
        </div>
      ) : (
        <CustomPolicyModalContent custom={custom} policy={policy} />
      )}
    </Modal>
  );
}
